import React, { Suspense, Fragment } from "react";
// import type { RouteObject } from "react-router-dom";
import { useLocation, matchRoutes, Navigate } from "react-router-dom";
import { Spin } from "antd";
import { RootStateI, RoutePropsI } from "@/types/system";
import {
  GiftOutlined,
  SettingOutlined,
  IdcardOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";

// 一个动态导入
// 为什么不写成lazyLoad(path: string)
// https://webpack.js.org/api/module-methods/#dynamic-expressions-in-import
function lazyLoad(Comp: React.LazyExoticComponent<any>): React.ReactNode {
  return (
    <Suspense
      fallback={
        <Spin
          size="large"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />
      }
    >
      <Comp />
    </Suspense>
  );
}

export const routes: RoutePropsI[] = [
  {
    path: "/",
    element: lazyLoad(React.lazy(() => import("@/pages/index"))),
  },
  {
    path: "/login-basic",
    element: lazyLoad(React.lazy(() => import("@/pages/login_basic"))),
  },
  {
    path: "/login",
    element: lazyLoad(React.lazy(() => import("@/pages/login"))),
  },
  {
    // 微信扫码登录，接收微信授权后的code，后台获取用户信息判断后续流程：
    path: "/wechat/oauth2/open",
    element: lazyLoad(React.lazy(() => import("@/pages/wechat_oauth2_open"))),
  },
  {
    // 微信公众号基础授权登录
    path: "/wechat/oauth2/base",
    element: lazyLoad(React.lazy(() => import("@/pages/oauth_base"))),
  },
  {
    // 微信公众号全权授权登录
    path: "/wechat/oauth2/userinfo",
    element: lazyLoad(React.lazy(() => import("@/pages/oauth_userinfo"))),
  },
  {
    // 系统管理
    path: "/system/",
    element: lazyLoad(React.lazy(() => import("@/layout/system_home"))),
    meta: {
      isMenu: true,
      icon: <SettingOutlined />,
      name: "系统管理",
      auth: true,
      roles: [],
      unRoles: [],
    },
    children: [
      {
        // 角色管理
        path: "/system/role-manage",
        element: lazyLoad(React.lazy(() => import("@/pages/role_manage"))),
        meta: {
          isMenu: true,
          // 此处icon一定要传用尖括号括起来的，否则报错：Uncaught Error: Objects are not valid as a React child (found: object with keys {$$typeof, render}). If you meant to render a collection of children, use an array instead.
          icon: <IdcardOutlined />,
          name: "角色管理",
          auth: true,
          roles: [],
          unRoles: [],
        },
      },
      {
        // 用户管理
        path: "/system/user-manage",
        element: lazyLoad(React.lazy(() => import("@/pages/user_manage"))),
        meta: {
          isMenu: true,
          // 此处icon一定要传用尖括号括起来的，否则报错：Uncaught Error: Objects are not valid as a React child (found: object with keys {$$typeof, render}). If you meant to render a collection of children, use an array instead.
          icon: <TeamOutlined />,
          name: "用户管理",
          auth: true,
          roles: [],
          unRoles: [],
        },
      },
    ],
  },
  {
    path: "/products",
    element: lazyLoad(React.lazy(() => import("@/pages/products"))),
    meta: {
      isMenu: true,
      // 此处icon一定要传用尖括号括起来的，否则报错：Uncaught Error: Objects are not valid as a React child (found: object with keys {$$typeof, render}). If you meant to render a collection of children, use an array instead.
      icon: <GiftOutlined />,
      name: "产品体验",
      auth: false,
      roles: [],
      unRoles: [],
    },
  },
  {
    path: "/diabetes/",
    element: lazyLoad(React.lazy(() => import("@/pages/diabetes"))),
    // element: lazyLoad(React.lazy(() => import("@/pages/patient_manage"))),
    meta: {
      isMenu: false,
      name: "糖尿病足管理",
      auth: false,
      roles: [],
      unRoles: [],
    },
    children: [
      {
        index: true,
        path: "/diabetes/",
        element: lazyLoad(React.lazy(() => import("@/pages/patient_manage"))),
        meta: {
          isMenu: false,
          name: "患者管理",
          auth: false,
          roles: [],
          unRoles: [],
        },
      },
      {
        index: true,
        path: "/diabetes/input",
        element: lazyLoad(React.lazy(() => import("@/pages/diabetes_input"))),
        meta: {
          isMenu: false,
          name: "资料录入",
          auth: false,
          roles: [],
          unRoles: [],
        },
      },
      {
        index: false,
        path: "/diabetes/history",
        element: lazyLoad(React.lazy(() => import("@/pages/history_manage"))),
        meta: {
          isMenu: false,
          name: "病史管理",
          auth: false,
          roles: [],
          unRoles: [],
        },
      },
      {
        index: false,
        path: "/diabetes/lab-exam",
        element: lazyLoad(React.lazy(() => import("@/pages/lab_exam_manage"))),
        meta: {
          isMenu: false,
          name: "检验检查",
          auth: false,
          roles: [],
          unRoles: [],
        },
      },
      {
        index: false,
        path: "/diabetes/follow-up",
        element: lazyLoad(React.lazy(() => import("@/pages/follow_up_manage"))),
        meta: {
          isMenu: false,
          name: "随访管理",
          auth: false,
          roles: [],
          unRoles: [],
        },
      },
    ],
  },
  {
    path: "/dtc",
    element: lazyLoad(React.lazy(() => import("@/pages/dtc"))),
    meta: {
      isMenu: false,
      name: "分化型甲状腺癌危险分层",
      auth: false,
      roles: [],
      unRoles: [],
    },
    // children: [
    //   {
    //     index: true,
    //     path: "/dtc/ata",
    //     element: lazyLoad(React.lazy(() => import("@/pages/dtc_ata"))),
    //     meta: {
    //       isMenu: false,
    //       name: "甲状腺癌复发风险",
    //       auth: false,
    //       roles: [],
    //       unRoles: [],
    //     },
    //   },
    //   {
    //     index: false,
    //     path: "/dtc/tnm-stage",
    //     element: lazyLoad(
    //       React.lazy(() => import("@/pages/dtc_tnm_stage"))
    //     ),
    //     meta: {
    //       isMenu: false,
    //       name: "甲状腺癌TNM分期",
    //       auth: false,
    //       roles: [],
    //       unRoles: [],
    //     },
    //   },
    // ],
  },
  {
    path: "/image",
    element: lazyLoad(React.lazy(() => import("@/layout/system_home"))),
    meta: {
      isMenu: false,
      // icon: <DatabaseOutlined />,
      name: "影像管理",
      auth: false,
      roles: [],
      unRoles: [],
    },
    children: [
      {
        index: true,
        path: "/image",
        element: lazyLoad(React.lazy(() => import("@/pages/image_manage"))),
        meta: {
          isMenu: false,
          name: "影像管理",
          auth: false,
          roles: [],
          unRoles: [],
        },
      },
      {
        path: "/image/:uploadedFileId/mark",
        element: lazyLoad(React.lazy(() => import("@/pages/image_mark"))),
        meta: {
          isMenu: false,
          name: "影像标注",
          auth: false,
          roles: [],
          unRoles: [],
        },
      },
    ],
  },
  // {
  //   path: "/chat",
  //   element: lazyLoad(React.lazy(() => import("@/pages/chat"))),
  //   meta: {
  //     isMenu: false,
  //     name: "大语言模型助手",
  //     auth: false,
  //     roles: [],
  //     unRoles: [],
  //   },
  // },
  {
    path: "*",
    element: lazyLoad(React.lazy(() => import("@/pages/e404"))),
  },
];

export const RouterAuth: React.FC = ({ children }) => {
  const user = useSelector((store: RootStateI) => store.user);
  const location = useLocation();
  // 匹配当前层级路由树
  const mathchs = matchRoutes(routes, location);
  // 建议打个断点这里调一下，matchs是返回的层级路由
  // 第一个元素为根路由 最后一个元素为当前路由
  // 所以我们从前往后匹配
  const isNeedLogin = mathchs?.some((item) => {
    const route: RoutePropsI = item.route;

    // 没有配置字段的直接返回
    if (!route.meta) return false;
    // 返回是否需要登录
    return route.meta.auth;
  });

  // console.log(user.token);
  if (isNeedLogin && !user.token) {
    console.log("需要登录");
    // 跳转到登录  state保存源路由
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // return children as React.ReactElement
  return <Fragment>{children}</Fragment>;
};
